// ─────────────────────────────────────────────────────────────────────────────
// ListPreferredProductsComponent
// ─────────────────────────────────────────────────────────────────────────────
// Paginated preferred-products table with search, filter, sort, checkbox
// selection, bulk delete, XLSX export, manual create/edit, and CSV import.
// Responsibilities:
//   - Dispatch LoadPreferredProducts and display the paginated result
//   - Debounced keyword search (customer + product)
//   - Customer, source, and date-range filter dialog
//   - Server-side column sorting (default: create_date desc)
//   - Checkbox selection for bulk delete
//   - XLSX export with current filters/sort applied
//   - Add/edit modal (manual + import rows; order-sourced are read-only)
//   - CSV import dialog (reloads list on successful import)
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { UiModule } from 'src/app/ui/ui.module';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { CommonService } from 'src/app/shared/common.service';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterFieldOption } from 'src/app/shared/components/filter-dialog/filter-dialog.models';

import { IPreferredProduct, PreferredProductFilters } from '../../models/preferred-product.models';
import { PreferredProductService } from '../../services/preferred-product.service';
import { PreferredProductModalComponent } from '../preferred-product-modal/preferred-product-modal.component';
import { ImportKnowledgeBaseDialogComponent } from '../import-knowledge-base-dialog/import-knowledge-base-dialog.component';
import { selectPreferredProductItems, selectPreferredProductLoading, selectPreferredProductMetadata } from '../../store/preferred-products.selectors';
import {
  BulkDeletePreferredProducts,
  BulkDeletePreferredProductsSuccess,
  CreatePreferredProductSuccess,
  LoadPreferredProducts,
  SetPreferredProductFilters,
  SetPreferredProductSearch,
  UpdatePreferredProductSuccess,
} from '../../store/preferred-products.actions';

type SortableField = 'customer' | 'product' | 'purchase_frequency' | 'source' | 'create_date' | 'last_seen_date';

@Component({
  selector: 'app-list-preferred-products',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-preferred-products.component.html',
  styleUrls: ['./list-preferred-products.component.scss'],
})
export class ListPreferredProductsComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  preferredProducts$: Observable<IPreferredProduct[]> = this.store.select(selectPreferredProductItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectPreferredProductMetadata);
  loading$: Observable<boolean> = this.store.select(selectPreferredProductLoading);

  // ── Local state ───────────────────────────────────────────────────────────

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  keyword = '';
  filters: PreferredProductFilters = { customer_id: null, source: null, create_date_from: null, create_date_to: null };

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IPreferredProduct>((row) => row.id);
  sortField: SortableField = 'create_date';
  sortMode: 'asc' | 'desc' = 'desc';
  sortedPreferredProducts: IPreferredProduct[] = [];
  searchControl = new FormControl('');
  activeFilterValues: Record<string, any> = {};

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private preferredProductService: PreferredProductService,
    private customersService: CustomersService,
    private commonService: CommonService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Sync preferred products from store to sortedPreferredProducts
    this.preferredProducts$.pipe(takeUntil(this.destroy$)).subscribe((rows) => {
      this.sortedPreferredProducts = [...rows];
      this.selection.setRows(this.sortedPreferredProducts);
    });

    // Debounced search input
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.onSearch(value ?? '');
    });

    // Reload only after bulk delete -- server removed rows
    this.actions$.pipe(ofType(BulkDeletePreferredProductsSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
      this.loadPreferredProducts();
    });

    // Reducer optimistically updates the list on create/update -- only clear selection
    this.actions$.pipe(ofType(CreatePreferredProductSuccess, UpdatePreferredProductSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
    });

    // Initial load (default: create_date desc)
    this.loadPreferredProducts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadPreferredProducts(): void {
    this.store.dispatch(
      LoadPreferredProducts({
        page: this.page,
        key: this.keyword || undefined,
        filters: this.filters,
        sortBy: this.sortField || undefined,
        sortMode: this.sortField ? this.sortMode : undefined,
      }),
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.selection.clear();
    this.loadPreferredProducts();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.keyword = value;
    this.selection.clear();
    this.store.dispatch(SetPreferredProductSearch({ keyword: value }));
    this.page = { ...this.page, number: 1 };
    this.loadPreferredProducts();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  private get preferredProductFilterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.preferred_products.filter.title',
        sections: [
          {
            title: 'knowledge_base.preferred_products.filter.group_title',
            fields: [
              {
                key: 'customer',
                label: 'filter.customer',
                type: 'autocomplete',
                placeholder: 'filter.customer_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.customersService
                    .listCustomers({ number: 1, size: 20 }, undefined, term)
                    .pipe(map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name })))),
              },
              {
                key: 'source',
                label: 'knowledge_base.preferred_products.col.source',
                type: 'single-select',
                options: [
                  { value: 'manual', label: this.translateService.instant('knowledge_base.preferred_products.source.manual') },
                  { value: 'import', label: this.translateService.instant('knowledge_base.preferred_products.source.import') },
                  { value: 'order', label: this.translateService.instant('knowledge_base.preferred_products.source.order') },
                ],
              },
              {
                key: 'create_date',
                label: 'filter.created_date',
                type: 'date-range',
                minKey: 'create_date_from',
                maxKey: 'create_date_to',
                minPlaceholder: 'common.from',
                maxPlaceholder: 'common.to',
              },
            ],
          },
        ],
      },
      values: {},
    };
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: {
        ...this.preferredProductFilterConfig,
        values: { ...this.activeFilterValues },
      } as FilterDialogData,
      width: '900px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Record<string, any> | undefined) => {
        if (result) {
          this.activeFilterValues = result;
          this.filters = {
            customer_id: result['customer']?.value ?? null,
            source: result['source'] || null,
            create_date_from: result['create_date_from'] || null,
            create_date_to: result['create_date_to'] || null,
          };
          this.store.dispatch(SetPreferredProductFilters({ filters: this.filters }));
          this.page = { ...this.page, number: 1 };
          this.loadPreferredProducts();
        }
      });
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.customer_id || this.filters.source || this.filters.create_date_from || this.filters.create_date_to);
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.filters = { customer_id: null, source: null, create_date_from: null, create_date_to: null };
    this.store.dispatch(SetPreferredProductFilters({ filters: this.filters }));
    this.page = { ...this.page, number: 1 };
    this.loadPreferredProducts();
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────

  onSort(field: SortableField): void {
    if (this.sortField === field) {
      this.sortMode = this.sortMode === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortMode = 'asc';
    }
    this.page = { ...this.page, number: 1 };
    this.loadPreferredProducts();
  }

  // ── Checkbox selection ────────────────────────────────────────────────────



  // ── Delete ────────────────────────────────────────────────────────────────

  bulkDelete(): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.store.dispatch(BulkDeletePreferredProducts({ ids: this.selection.ids }));
        }
      });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  exportList(): void {
    this.preferredProductService
      .exportPreferredProducts(
        'xlsx',
        this.keyword || undefined,
        this.filters,
        this.sortField || undefined,
        this.sortField ? this.sortMode : undefined,
      )
      .pipe(
        catchError(() => {
          this.commonService.showToast(this.translateService.instant('knowledge_base.preferred_products.toast.export_error'), 'ERROR');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `preferred_products_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  // ── Dialogs ───────────────────────────────────────────────────────────────

  openCreateModal(): void {
    this.dialog.open(PreferredProductModalComponent, {
      data: null,
      disableClose: true,
    });
  }

  openEditModal(row: IPreferredProduct): void {
    if (!this.canEdit(row)) return;
    this.dialog.open(PreferredProductModalComponent, {
      data: row,
      disableClose: true,
    });
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(ImportKnowledgeBaseDialogComponent, {
      data: { type: 'preferred-product' },
      disableClose: true,
      width: '700px',
      maxWidth: '95vw',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.loadPreferredProducts();
        }
      });
  }

  canEdit(row: IPreferredProduct): boolean {
    return row.source !== 'order';
  }
}
